import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Loader2, FileBarChart, Users, Receipt, TrendingDown, ShieldCheck, CalendarDays, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const EXP_LABEL = {
  gasolina: "Gasolina",
  material_escola: "Materiais",
  funcionarios: "Funcionários",
  locacao: "Locação",
  servicos_terceirizados: "Serviços terceirizados",
  ferramentas: "Ferramentas",
  alimentacao: "Alimentação",
  impostos: "Impostos",
  outros: "Outros",
};

const GeneralExpensesSection = ({ data }) => (
  <section className="bg-white rounded-2xl border border-orange-200 p-5 card-shadow" data-testid="monthly-general-expenses">
    <div className="flex items-center gap-2 mb-1">
      <Receipt className="w-4 h-4 text-orange-600" />
      <h2 className="font-heading text-base font-black text-slate-900 flex-1">Saídas diversas (despesas gerais do mês)</h2>
      <span className="font-black tabular-nums text-orange-700" data-testid="monthly-general-total">{BRL(data?.general_expenses_total || 0)}</span>
    </div>
    <p className="text-[11px] text-slate-500 mb-3">Despesas sem orçamento vinculado. Descontam só do resultado do mês — não alteram sócios nem caixa.</p>
    {(data?.general_expenses || []).length === 0 ? (
      <p className="text-sm text-slate-500 italic">Nenhuma despesa geral no mês.</p>
    ) : (
      <>
        <ul className="space-y-1.5 mb-3">
          {Object.entries(data.general_expenses_breakdown || {}).map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-[13px]">
              <span className="text-slate-700">{EXP_LABEL[k] || k}</span>
              <span className="font-black tabular-nums text-slate-900">{BRL(v)}</span>
            </li>
          ))}
        </ul>
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {data.general_expenses.map((e) => (
            <li key={e.id} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`monthly-general-${e.id}`}>
              <span className="text-slate-500 tabular-nums shrink-0">{formatDateBR(e.date)}</span>
              <span className="flex-1 min-w-0 truncate text-slate-700">{e.description || EXP_LABEL[e.category] || e.category}</span>
              <span className="font-bold tabular-nums">{BRL(e.amount)}</span>
            </li>
          ))}
        </ul>
      </>
    )}
  </section>
);

const downloadMonthlyCsv = (data, month) => {
  if (!data?.items?.length) return;
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = [
    "Nº Orçamento", "Data NF", "NF", "Escola", "Bruto", "ISS", "Simples",
    "Despesas", "Líquido", "Reserva",
    ...(data.socios || []).map((s) => `Sócio: ${s.name}`),
  ];
  const rows = data.items.map((r) => {
    const base = [
      r.budget_number, r.nf_date, r.nf_number, r.school_name,
      r.gross, r.iss_value, r.simples_value, r.expenses_total,
      r.net_after_taxes_expenses, r.reserve_value,
    ];
    const bySocio = (data.socios || []).map((s) => {
      const d = (r.distributions || []).find((x) => x.socio_id === s.id);
      return d ? d.value : 0;
    });
    return [...base, ...bySocio].map(esc).join(";");
  });
  const csv = [header.map(esc).join(";"), ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `consolidado-${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const Stat = ({ label, value, hint, tone = "slate" }) => {
  const tones = {
    slate: "bg-white border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <p className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</p>
      <p className="mt-1.5 text-base sm:text-lg font-black tabular-nums">{value}</p>
      {hint && <p className="text-[10px] opacity-70 mt-0.5">{hint}</p>}
    </div>
  );
};

export default function FinanceMonthly() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [params] = useSearchParams();
  const qMonth = params.get("month");
  const [month, setMonth] = useState(/^\d{4}-\d{2}$/.test(qMonth || "") ? qMonth : defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const r = await api.get("/finance/monthly", { params: { month: m } });
      setData(r.data || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(month); }, [month]);

  return (
    <div className="space-y-5 max-w-5xl" data-testid="finance-monthly-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
          <FileBarChart className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">
            Consolidado mensal
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Totais do mês por categoria, com reserva de caixa e distribuição.
          </p>
        </div>
      </div>

      {/* Month picker */}
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarDays className="w-4 h-4 text-slate-500" />
        <Input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-9 max-w-[180px] text-[13px]"
          data-testid="month-picker"
        />
        <span className="text-[11px] text-slate-500">
          {data?.count || 0} orçamento(s)
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadMonthlyCsv(data, month)}
          disabled={!data?.items?.length}
          data-testid="btn-export-monthly-csv"
          className="ml-auto h-9 text-[12px] font-bold"
        >
          <Download className="w-4 h-4 mr-1" />
          Exportar CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Receita bruta" value={BRL(data?.gross || 0)} tone="emerald" />
            <Stat label="ISS" value={BRL(data?.iss || 0)} tone="rose" />
            <Stat label="Simples Nacional" value={BRL(data?.simples || 0)} tone="rose" />
            <Stat label="Despesas dos orçamentos" value={BRL(data?.expenses_total || 0)} tone="rose" />
            <Stat label="Resultado dos orçamentos" value={BRL(data?.net || 0)} tone="slate" hint="após impostos e despesas" />
            <Stat label="Saídas diversas" value={BRL(data?.general_expenses_total || 0)} tone="rose" hint="despesas gerais do mês" />
            <Stat label="Resultado do mês" value={BRL(data?.net_after_general || 0)} tone={(data?.net_after_general || 0) < 0 ? "rose" : "emerald"} hint="já descontadas as saídas diversas" />
            <Stat label="Reserva caixa" value={BRL(data?.reserve || 0)} tone="sky" />
            <Stat label="Conta do dia a dia" value={BRL(data?.dia_a_dia || 0)} tone="slate" hint="fica para Simples e gastos" />
            <Stat label="Distribuído sócios" value={BRL(data?.distributable || 0)} tone="emerald" />
            <Stat label="Total orçamentos" value={data?.count || 0} tone="slate" />
          </div>

          {/* Expense breakdown */}
          <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="w-4 h-4 text-rose-600" />
              <h2 className="font-heading text-base font-black text-slate-900">
                Despesas dos orçamentos por categoria
              </h2>
            </div>
            <ul className="space-y-1.5">
              {Object.entries(data?.expenses_breakdown || {}).map(([k, v]) => (
                <li key={k} className="flex items-center justify-between text-[13px]">
                  <span className="text-slate-700">{EXP_LABEL[k] || k}</span>
                  <span className="font-black tabular-nums text-slate-900">{BRL(v)}</span>
                </li>
              ))}
            </ul>
          </section>

          <GeneralExpensesSection data={data} />

          {data?.courtesy?.count > 0 && (
            <section className="bg-white rounded-2xl border border-pink-200 p-5 card-shadow" data-testid="monthly-courtesy">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-heading text-base font-black text-slate-900 flex-1">Cortesias ({data.courtesy.count})</h2>
                <span className="font-black tabular-nums text-pink-700">{BRL(data.courtesy.total)}</span>
              </div>
              <p className="text-[11px] text-slate-500 mb-3">Notas pagas marcadas como cortesia: fora da receita, do caixa e dos sócios. Os impostos ({BRL(data.courtesy.taxes)}) entram no DAS.</p>
              <ul className="divide-y divide-slate-100">
                {data.courtesy.items.map((i) => (
                  <li key={i.budget_id} className="py-1.5 flex items-center gap-2 text-xs">
                    <span className="font-bold text-slate-800 shrink-0">Nº {i.number}</span>
                    <span className="flex-1 min-w-0 truncate text-slate-600">{i.school_name}</span>
                    <span className="tabular-nums font-bold">{BRL(i.total)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Socios */}
          <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-emerald-600" />
              <h2 className="font-heading text-base font-black text-slate-900">
                Distribuição aos sócios
              </h2>
            </div>
            {(data?.socios || []).length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nenhuma distribuição no período.</p>
            ) : (
              <ul className="space-y-2">
                {data.socios.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3"
                    data-testid={`monthly-socio-${s.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-black text-slate-900 leading-tight truncate">
                        {s.name}
                      </p>
                      <p className="text-[10.5px] text-emerald-700">
                        {s.count} orçamento(s) no mês
                      </p>
                    </div>
                    <p className="text-sm font-black tabular-nums text-emerald-700">{BRL(s.total)}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Items table */}
          <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-4 h-4 text-slate-700" />
              <h2 className="font-heading text-base font-black text-slate-900">
                Orçamentos do mês
              </h2>
            </div>
            {(data?.items || []).length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nenhum orçamento com NF no período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500 text-left">
                    <tr>
                      <th className="pb-2">NF</th>
                      <th className="pb-2">Escola</th>
                      <th className="pb-2 text-right">Bruto</th>
                      <th className="pb-2 text-right">Líquido</th>
                      <th className="pb-2 text-right">Reserva</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="py-2 whitespace-nowrap">
                          <p className="font-black text-slate-900">#{r.budget_number}</p>
                          <p className="text-[10px] text-slate-500">{formatDateBR(r.nf_date)}</p>
                        </td>
                        <td className="py-2 truncate max-w-[200px]">{r.school_name}</td>
                        <td className="py-2 text-right tabular-nums font-semibold">{BRL(r.gross)}</td>
                        <td className="py-2 text-right tabular-nums">{BRL(r.net_after_taxes_expenses)}</td>
                        <td className="py-2 text-right tabular-nums text-sky-700">{BRL(r.reserve_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
